import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IBrand,
  type IBrandSafetyOperators,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type IBrandState } from './types';

export const preloadedState: IBrandState = {
  ...asyncStateInitial,
  brand: undefined,
  brands: undefined,
  safetyOperators: undefined,
};

const brandSlice = createSlice({
  name: 'Brand',
  initialState: preloadedState,
  reducers: {
    brandAdd: {
      reducer(state) {
        startLoading(state);
      },
      prepare(name: string) {
        return { payload: { name } };
      },
    },
    brandsLoadAll(state) {
      startLoading(state);
    },
    brandsLoadAllResponse: {
      reducer(state, action: { payload: { brands: IBrand[] } }) {
        state.brands = action.payload.brands.sort(
          (a: IBrand, b: IBrand): number => stringCompare(a.name, b.name),
        );
        finishLoading(state);
      },
      prepare(brands: IBrand[]) {
        return { payload: { brands } };
      },
    },
    brandLoad: {
      reducer(state) {
        startLoading(state);
        state.brand = undefined;
        state.safetyOperators = undefined;
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    brandLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            brand: IBrand | null;
            safetyOperators: IBrandSafetyOperators | null;
          };
        },
      ) {
        state.brand = action.payload.brand;
        state.safetyOperators = action.payload.safetyOperators;
        finishLoading(state);
      },
      prepare(
        brand: IBrand | null,
        safetyOperators: IBrandSafetyOperators | null = null,
      ) {
        return { payload: { brand, safetyOperators } };
      },
    },
    brandUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(
        uuid: string,
        name: string,
        safetyOperators: IBrandSafetyOperators | null = null,
      ) {
        return { payload: { uuid, name, safetyOperators } };
      },
    },
    brandUpdateResponse: {
      reducer(
        state,
        action: {
          payload: {
            brand: IBrand | null;
            safetyOperators: IBrandSafetyOperators | null;
          };
        },
      ) {
        state.brand = action.payload.brand;

        if (action.payload.safetyOperators) {
          state.safetyOperators = action.payload.safetyOperators;
        }

        finishLoading(state);

        if (action.payload.brand && Array.isArray(state.brands)) {
          const idx = state.brands.findIndex(
            (b: IBrand): boolean => b.uuid === action.payload.brand!.uuid,
          );
          if (idx !== -1) {
            state.brands[idx] = action.payload.brand;
          }
        }
      },
      prepare(
        brand: IBrand | null,
        safetyOperators: IBrandSafetyOperators | null = null,
      ) {
        return { payload: { brand, safetyOperators } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Brand'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  brandAdd,
  brandsLoadAll,
  brandsLoadAllResponse,
  brandLoad,
  brandLoadResponse,
  brandUpdate,
  brandUpdateResponse,
} = brandSlice.actions;
export default brandSlice.reducer;
